import React, { useState } from 'react'
import moment from 'moment'
import { Link } from 'react-router-dom'

import styles from '../component-styles/calendar-date-details.module.scss'
import Button from './button'
import AddEvent from './calendar-add-event'
import CreateCampaign from './calendar-create-campaign'
import { formatForBreakdownLink } from '../helpers/dates'
import { cap } from '../helpers'

interface Props {
  date: Date | null
  events?: EventCalendarItem[] | null
  anomalies?: AnomalyCalendarItem[] | null
  campaigns?: CampaignCalendarItem[] | null
  gaCampaigns: string[]
  onSuccessfullyAddingEvent: () => void
  onSuccessfullyCreatingCampaign: () => void
}

export default function DateDetails({
  date,
  events,
  anomalies,
  campaigns,
  gaCampaigns,
  onSuccessfullyAddingEvent,
  onSuccessfullyCreatingCampaign,
}: Props): React.ReactElement | null {
  const [eventModalActive, setEventModalActive] = useState(false)
  const [campaignModalActive, setCampaignModalActive] = useState(false)
  const [selectedEvent, setSelectedEvent]: [any, any] = useState(null)
  const [selectedCampaign, setSelectedCampaign]: [any, any] = useState(null)

  if (!date) {
    return (
      <div aria-label="date details" className={styles.container}>
        <h3 className={styles.heading}>Detail</h3>
      </div>
    )
  }

  return (
    <div aria-label="date details" className={styles.container}>
      <h3 aria-label="active date">{moment(date).format('ddd DD MMM YYYY')}</h3>
      <Button
        onClick={(): void => {
          setSelectedEvent(null)
          setEventModalActive(!eventModalActive)
        }}
        ariaLabel="toggle-event-modal"
        type="containerBlue"
      >
        Add event
      </Button>

      <Button
        onClick={(): void => setCampaignModalActive(true)}
        ariaLabel="toggle-campaign-modal"
        type="containerYellow"
      >
        Add campaign
      </Button>

      <h3 className={styles.heading}>Detail</h3>

      {anomalies && (
        <ul aria-label="anomalies" className={styles.anomalyList}>
          {anomalies.map(
            ({
              metricID,
              displayName = '',
            }: AnomalyCalendarItem): React.ReactElement => {
              return (
                <li key={`${metricID}/${formatForBreakdownLink(date)}`}>
                  <span>
                    <Link
                      to={`/explain/anomaly/${metricID}/${formatForBreakdownLink(
                        date,
                      )}`}
                    >
                      {cap(displayName)} Anomaly: Click to explain
                    </Link>
                  </span>
                </li>
              )
            },
          )}
        </ul>
      )}

      {events && (
        <ul aria-label="events" className={styles.eventsList}>
          {events.map(
            (event): React.ReactElement => (
              <li key={`${event.eventID}${event.eventTitle}`}>
                <span
                  onClick={(): void => {
                    setSelectedEvent(event)
                    setEventModalActive(true)
                  }}
                  onKeyDown={(): void => {
                    setSelectedEvent(event)
                    setEventModalActive(true)
                  }}
                  role="button"
                  tabIndex={-1}
                >
                  Event: {event.eventData}
                </span>
              </li>
            ),
          )}
        </ul>
      )}

      {campaigns && (
        <ul aria-label="campaigns" className={styles.campaignsList}>
          {campaigns.map(
            (campaign): React.ReactElement => (
              <li key={`${campaign.eventID}${campaign.startDate}`}>
                <span
                  onClick={(): void => {
                    setSelectedCampaign(campaign)
                    setCampaignModalActive(true)
                  }}
                  onKeyDown={(): void => {
                    setSelectedCampaign(campaign)
                    setCampaignModalActive(true)
                  }}
                  role="button"
                  tabIndex={-1}
                >
                  Campaign: {campaign.campaignData}
                </span>
              </li>
            ),
          )}
        </ul>
      )}

      {eventModalActive && (
        <AddEvent
          active={eventModalActive}
          toggleActive={(): void => setEventModalActive(!eventModalActive)}
          onSuccess={onSuccessfullyAddingEvent}
          date={date}
          event={selectedEvent}
        />
      )}

      {campaignModalActive && (
        <CreateCampaign
          active={campaignModalActive}
          toggleActive={(): void => {
            setCampaignModalActive(!campaignModalActive)
            setSelectedCampaign(null)
          }}
          onSuccess={onSuccessfullyCreatingCampaign}
          startDate={date}
          gaCampaigns={gaCampaigns}
          campaign={selectedCampaign}
        />
      )}
    </div>
  )
}
